import { InfoIcon } from '@/assets/icons/info-icons/info';
import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import { Banner } from '@/components/basicComponents/banner';
import ColorPicker from '@/components/basicComponents/colorPicker';
import { FormFooter } from '@/components/basicComponents/formFooter';
import ModalLoader from '@/components/basicComponents/modal/modalLoader';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import Input from '@/components/basicComponents/input';
import Header from '@/components/header';
import { GoBackButton } from '@/components/header/goBackButton';
import { PageTitle } from '@/components/header/pageTitle';
import { SettingsContainer, SettingsSection } from '@/components/settings-page';
import { useErrorMessages } from '@/hooks/i18n';
import { useResponsive } from '@/hooks/use-responsive';
import { useClickOutside } from '@/hooks/useClickOutside';
import { MainWrap } from '@/styles/common';
import { themeVar } from '@/styles/theme';
import { useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import styled from 'styled-components';
import { IFirmSettings, IFontStyle } from '../types';

interface EditAccentColorProps {
  currentRow: IFirmSettings | null;
  saveSettingValue: (key: string, value: string | IFontStyle) => Promise<void>;
  handleClose: () => void;
}

export const EditAccentColor = ({ currentRow, saveSettingValue, handleClose }: EditAccentColorProps) => {
  const { isMobile } = useResponsive();
  const { default: defaultErrorMessage } = useErrorMessages();
  const { control, reset, handleSubmit } = useForm({ defaultValues: { accentColor: '' } });

  const [isColorPickerOpen, setIsColorPickerOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const colorPickerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (currentRow?.value) {
      reset({ accentColor: currentRow.value.replace('#', '') });
    }
  }, [currentRow]);

  const handleColorPicker = () => {
    setIsColorPickerOpen(!isColorPickerOpen);
  };

  const saveValue = async (data: { accentColor: string }) => {
    try {
      const { accentColor } = data;
      setErrorMessage(null);
      setLoading(true);

      await saveSettingValue(currentRow?.key, `#${accentColor}`);

      handleClose();
    } catch (error) {
      setErrorMessage(defaultErrorMessage);
    } finally {
      setLoading(false);
    }
  };

  useClickOutside<HTMLDivElement>(colorPickerRef, handleColorPicker);

  return (
    <>
      {loading && (
        <ModalWrappedContent
          isOpen={loading}
          title="Saving"
          showCancelButton={false}
          showCloseIcon={false}
          isUseOutsideClick={false}
          onClose={() => {}}
        >
          <ModalLoader />
        </ModalWrappedContent>
      )}
      <MainWrap>
        <Header modalControl={<GoBackButton handleClose={handleClose} backToTitle="Templates" />} />
        <PageTitle title={currentRow?.fieldName ?? ''} />
      </MainWrap>
      <MainWrap>
        {errorMessage && (
          <Banner
            icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
            title="Error"
            description={errorMessage}
            bgColor={themeVar('context.error')}
          />
        )}
        <SettingsContainer>
          <SettingsSection isMobile={isMobile}>
            <Banner
              title=""
              description="Choose a darker color that works well on a white or light colored background and with superimposed white text."
              icon={<InfoIcon width={26} height={26} fill={themeVar('header.base')} />}
              bgColor={themeVar('layer.base')}
              color={themeVar('font.base')}
              dividerColor={themeVar('border.base')}
            />
            <FourColumnsWrapper isMobile={isMobile}>
              <Controller
                name="accentColor"
                control={control}
                rules={{
                  pattern: {
                    value: /^([0-9A-F]{3}){1,2}([0-9A-F]{2})?$/i,
                    message: 'Invalid HEX color'
                  }
                }}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <InputWrapper>
                    <Input value={value || ''} onChange={onChange} label="Accent Color" errorText={error?.message} />
                    <ColorPreview bgColor={`#${value}`} onClick={handleColorPicker} />
                    {isColorPickerOpen && (
                      <Popover ref={colorPickerRef}>
                        <PopoverContent>
                          <ColorPicker color={value} onChange={onChange} />
                        </PopoverContent>
                      </Popover>
                    )}
                  </InputWrapper>
                )}
              />
            </FourColumnsWrapper>
          </SettingsSection>
          <FormFooter onCancel={handleClose} onSave={handleSubmit(saveValue)} disableSaveButton={loading} showRequiredFieldsWarning={false} />
        </SettingsContainer>
      </MainWrap>
    </>
  );
};

const FourColumnsWrapper = styled.div<{ isMobile: boolean }>`
  display: grid;
  grid-template-columns: ${({ isMobile }) => (isMobile ? '1fr' : 'repeat(4, 1fr)')};
  gap: ${({ isMobile }) => (isMobile ? '15px' : '30px')};
  padding-top: 30px;
`;

const InputWrapper = styled.div`
  position: relative;
`;

const ColorPreview = styled.div<{ bgColor: string }>`
  position: absolute;
  top: 49px;
  right: 12px;
  width: 24px;
  height: 24px;
  cursor: pointer;
  border-radius: 20px;
  border: 1px solid ${themeVar('border.base')};
  background-color: ${({ bgColor }) => bgColor};
`;

const Popover = styled.div`
  position: absolute;
  z-index: 2;
  top: 100px;
  left: 0;
`;

const PopoverContent = styled.div`
  position: relative;
  border-radius: 10px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.2);
`;
